export const buildVersionLabel = (versionNumber) =>
  `v${String(Number(versionNumber) || 1).padStart(3, "0")}`;

// PO1-2783 — só há "versões" quando o grupo tem mais de um doc: a versão
// corrente com versionNumber > 1, ou uma versão antiga (isLatestVersion=false).
export const hasVersions = (emb) =>
  !!emb && (Number(emb.versionNumber) > 1 || emb.isLatestVersion === false);

const normalizeName = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

// PO1-2790 — acha, dentro de uma lista de ofertas (default: todo emb.cotacoes),
// a que corresponde à transportadora selecionada no embarque (por
// idTransportadora, com fallback por nome normalizado). Reaproveitado por
// isNotLowestPrice (passando a lista já filtrada por valor > 0, para preservar
// o comportamento original) e pela listagem principal (CotacaoListItem, sem
// filtro, para exibir frete/prazo da proposta escolhida mesmo se valor <= 0).
export const findCotacaoSelecionada = (emb, ofertas) => {
  if (!emb) return null;
  const lista = Array.isArray(ofertas)
    ? ofertas.filter(Boolean)
    : (Array.isArray(emb.cotacoes) ? emb.cotacoes.filter(Boolean) : []);
  if (lista.length === 0) return null;

  if (emb.transportadoraId !== null && emb.transportadoraId !== undefined) {
    const porId = lista.find(
      (c) =>
        c.idTransportadora !== null &&
        c.idTransportadora !== undefined &&
        String(c.idTransportadora) === String(emb.transportadoraId)
    );
    if (porId) return porId;
  }

  const nome = normalizeName(emb.transportadora);
  if (nome) {
    const porNome = lista.find((c) => normalizeName(c.transportadora) === nome);
    if (porNome) return porNome;
  }

  return null;
};

// PO1-2713 — a transportadora selecionada não tem a menor proposta em
// cotacoes[]. Informativo: não bloqueia nada. Com menos de 2 ofertas
// válidas (valor > 0) não há comparação possível.
export const isNotLowestPrice = (emb) => {
  if (!emb) return false;
  const ofertas = (Array.isArray(emb.cotacoes) ? emb.cotacoes : []).filter(
    (c) => c && Number(c.valor) > 0
  );
  if (ofertas.length < 2) return false;

  const menor = Math.min(...ofertas.map((c) => Number(c.valor)));

  const selecionada = findCotacaoSelecionada(emb, ofertas);
  const valorSel = selecionada ? Number(selecionada.valor) : Number(emb.valor_total);
  if (!(valorSel > 0)) return false;
  return valorSel > menor;
};

export const ALLOWED_TRANSITIONS = {
  Pendente: ["Stand-by", "Simulado", "Realizado"],
  "Stand-by": ["Simulado", "Realizado"],
  Simulado: ["Stand-by", "Realizado"],
  Realizado: [],
};

export const canTransition = (from, to) =>
  (ALLOWED_TRANSITIONS[from] || []).includes(to);

export const isEditBlocked = (status) =>
  status === "Simulado" || status === "Realizado";

// PO1-2781/2783 — "+ Criar nova versão" nasce de Stand-by ou de Aprovado
// (Simulado). Pendente ainda não fechou uma versão; Realizado é imutável.
export const canCreateVersion = (status) =>
  status === "Stand-by" || status === "Simulado";

export const calculateQuoteTotal = async (quotePayload) => {
  const base = Number(quotePayload?.valorNF) || 0;
  const peso = Number(quotePayload?.peso) || 0;
  const densidade = Number(quotePayload?.densidade) || 0;
  const cubicWeight = Number(quotePayload?.m3) || 0;

  const logisticsFactor = peso * 0.35 + densidade * 10 + cubicWeight * 0.2;
  return Number((base + logisticsFactor).toFixed(2));
};

export const saveQuote = async (quotePayload, existingVersions = []) => {
  const nextVersion = existingVersions.length + 1;
  const providedTotal = Number(quotePayload?.valor_total);
  const calculatedTotal = Number.isNaN(providedTotal)
    ? await calculateQuoteTotal(quotePayload)
    : providedTotal;

  const version = {
    id: buildVersionLabel(nextVersion),
    createdAt: new Date().toISOString(),
    status: quotePayload.status || "Pendente",
    valor_total: calculatedTotal,
    snapshot: { ...quotePayload },
  };

  return { version, valor_total: calculatedTotal };
};
